import { Box, Typography } from '@mui/material'
import { ListingPage } from 'components/ListingPage/ListingPage'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { Column } from 'components/Table/Table'
import { useGetMasterProducts } from 'hooks/useProducts'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { MasterProduct } from 'types/api/product'
import i18n from 'lib/i18'
import { contentLanguages } from 'lib/languages'
import { ProductsHeader } from './ProductsHeader'
import { ProductImage } from './ProductImage'

export const Products = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location = useLocation()
  const parameters = new URLSearchParams(location.search)
  const page = parameters.get('page')
  const entries = parameters.entries()
  const paramInfo = Array.from(entries).filter((value: string[]) => {
    return value[0] !== 'page'
  })
  const queryKey = paramInfo.length > 0 ? paramInfo[0][0] : undefined
  const query = paramInfo.length > 0 ? paramInfo[0][1] : undefined

  const rowsPerPage = 15

  const { isFetching, error, products, pagination } = useGetMasterProducts(
    Number(page) || 1,
    rowsPerPage,
    query,
    queryKey
  )

  const columns: Column<'name' | 'gtin' | 'brand' | 'image'>[] = [
    { id: 'image', label: '', minWidth: 50, cellWidth: '50px' },
    { id: 'name', label: t('productName'), minWidth: 170 },
    { id: 'gtin', label: t('GTIN'), minWidth: 170 },
    { id: 'brand', label: t('brand'), minWidth: 170 },
  ]

  const getValue = (product: MasterProduct, id: string) => {
    const data = id.split('.').reduce((p, prop) => {
      return p[prop as keyof typeof product] as unknown as MasterProduct
    }, product)
    if (id === 'name') {
      const nameOptions =
        product.tradeItem.tradeItemInformation.extensions
          .tradeItemDescriptionModule.tradeItemDescriptionInformation
          .descriptionShort
      if (!nameOptions) return ''
      const nameInLang =
        nameOptions.find((name) => name['@languageCode'] === i18n.language) ||
        nameOptions.find(
          (name) => name['@languageCode'] === contentLanguages[0]
        )
      return <Typography variant='body2'>{nameInLang?.$ || ''}</Typography>
    }
    if (id === 'brand') {
      const brandOptions =
        product.tradeItem.tradeItemInformation.extensions
          .tradeItemDescriptionModule?.tradeItemDescriptionInformation
          ?.brandNameInformation?.languageSpecificBrandName
      const brandInLang = brandOptions
        ? brandOptions.find(
            (brand) => brand['@languageCode'] === i18n.language
          ) ||
          brandOptions.find(
            (brand) => brand['@languageCode'] === contentLanguages[0]
          )
        : null
      const brand = brandInLang
        ? brandInLang?.$
        : product.tradeItem.tradeItemInformation.extensions
            .tradeItemDescriptionModule?.tradeItemDescriptionInformation
            ?.brandNameInformation?.brandName
      return <Typography>{brand || ''}</Typography>
    }
    if (id === 'image') {
      const imgInfo =
        product.tradeItem.tradeItemInformation.extensions.dgMediaModule
          ?.media || []
      return (
        <ProductImage
          imgInfo={imgInfo?.length > 0 ? imgInfo[0].mediaStorageKey : ''}
        />
      )
    }

    return data
  }

  const handleSearch = (key: string, query: string | undefined) => {
    if (!query) {
      navigate('/product')
    }
    if (query) {
      navigate(`/product?${key}=${query}`)
    }
  }

  const handleNavigate = (id: string) => {
    localStorage.setItem('productsListingPage', location.search)
    navigate(`/product/${id}/descriptive-information`)
  }

  const handleChangePage = (newPage: number) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      if (query) {
        navigate(`/product?page=${newPage}&${queryKey}=${query}`)
      } else {
        navigate(`/product?page=${newPage}`)
      }
    }
  }

  if (isFetching) return <LoadingIndicator />
  if (error) return <h1>Error</h1>
  return (
    <Box p={4}>
      <ListingPage
        tableWidth='calc(100vw - 64px - 40px)'
        columns={columns}
        listItems={products}
        handleNavigate={handleNavigate}
        header={
          <ProductsHeader
            query={query}
            hasListItems={Boolean(products && products.length > 0)}
            handleSearch={handleSearch}
          />
        }
        query={query}
        pagination={pagination}
        page={Number(page) || 1}
        handleChangePage={handleChangePage}
        displayValues={getValue}
      />
    </Box>
  )
}
